import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Text } from '../../../../builder-io/storyblocks-design-system/components/atoms/Text/Text';
import useMedia from '../../../../common/hooks/useMedia';
import { searchOrigins } from '../../../Navigation/constants';
import { SearchFilterContentTypes } from '../../../Shared/enums';
import { SearchOption } from '../../../Shared/types';
import { useSearchWithAI } from '../../hooks/useSearchWithAI';
import { saveSearchHistory } from '../navSearch/AutosuggestElements/util';
import {
  NavSearchContext,
  NavSearchContextProvider,
  NavSearchProps,
} from '../navSearch/NavSearchContext';
import CrossAssetNavSearchBox from '../navSearch/NavSearchElements/CrossAssetNavSearchBox';
import NavSearchBox from '../navSearch/NavSearchElements/NavSearchBox';
import { cleanSearchKeywords } from '../navSearch/NavSearchElements/searchUtils';
import ProminentSearchDropdown from '../navSearch/ProminentSearchElements/ProminentSearchDropdown';
import AISearchOption from './ConversationalSearchElements/AISearchOption';
import RightArrowButton from './ConversationalSearchElements/RightArrowButton';
import SearchWithAIButton from './ConversationalSearchElements/SearchWithAIButton';
import { AI_SEARCH_OPTIONS } from './ConversationalSearchElements/aiSearchOptions';
import ProminentSearchButton from './ProminentSearchButton';

export interface ProminentSearchProps extends NavSearchProps {
  onDropdownChange: (contentType: string) => void;
  searchTerm: string;
  prominentSearchOptions: SearchOption[];
  contentTypeFromPrevious: SearchFilterContentTypes;
  showDropDown: boolean;
  showContentToggles?: boolean;
  isProminentSearch?: boolean;
  showConversationalSearchCta?: boolean;
}

export default function ProminentSearchNav(props: ProminentSearchProps) {
  const {
    onDropdownChange,
    onSubmit,
    userHasAnySubscription,
    prominentSearchOptions,
    contentTypeFromPrevious,
    showDropDown,
    showContentToggles = false,
    useSnapshots = false,
    showConversationalSearchCta = false,
  } = props;

  const defaultSearchOption =
    prominentSearchOptions?.[0]?.id ?? contentTypeFromPrevious;

  const [contentType, setContentType] = useState(
    defaultSearchOption ?? contentTypeFromPrevious
  );
  const [searchTerm, setSearchTerm] = useState(props.searchTerm ?? '');
  const {
    showAiCta,
    conversationalSearchEnabled,
    setConversationalSearchEnabled,
    getPlaceholder,
    handleConversationalRedirect,
  } = useSearchWithAI({ showConversationalSearchCta });

  // Ensure searchTerm stays up to date when given a new one via props.
  useEffect(() => {
    setSearchTerm(props.searchTerm ?? '');
  }, [props.searchTerm]);

  useEffect(() => {
    setContentType(contentTypeFromPrevious);
  }, [contentTypeFromPrevious]);

  const isMediumScreen = useMedia('(min-width: 672px)', true);

  const handleDropdownChange = (contentType) => {
    onDropdownChange(contentType);
    setContentType(contentType);
  };

  const makeEmptySuggestions = () => ({
    keywords: [],
    relatedCollections: [],
    latestCollections: [],
    trendingCollections: [],
    relatedContentTypesAndKeywords: [],
  });

  const [suggestions, setSuggestions] = useState(makeEmptySuggestions());

  const clearSuggestions = useCallback(
    () => setSuggestions(makeEmptySuggestions()),
    []
  );
  const handleSubmit = (
    contentTypeSearched: SearchFilterContentTypes = contentType,
    value: string = searchTerm,
    searchOriginOverride: string = ''
  ) => {
    const cleanedSearchKeywords = cleanSearchKeywords(value);
    const searchOrigin = searchOriginOverride || searchOrigins.SEARCH_BAR;

    if (props?.enableSearchHistory) {
      saveSearchHistory(contentTypeSearched, cleanedSearchKeywords);
    }

    if (handleConversationalRedirect(cleanedSearchKeywords, searchOrigin)) {
      return;
    }

    onSubmit(contentTypeSearched, cleanedSearchKeywords, searchOrigin);
  };

  const activeTab = 'bg-white text-gray-800 thick-3 offset-5';
  // Use rgba() instead of bg-opacity-* which was removed in Tailwind v4
  // and conflicts when the shell (v4) composes this StockBlocks (v2) page.
  const inactiveTab = 'text-white';

  return (
    <NavSearchContextProvider {...props}>
      <NavSearchContext.Consumer>
        {({ setShowSuggestions }) => (
          <div
            className={twMerge(
              'flex flex-col w-full font-inter text-gray-900 items-center'
            )}
          >
            {showContentToggles && (
              <div className="product m-0 flex text-base font-bold grid-rows-4 pb-4 md:pb-6 lg:pb-6 cursor-pointer gap-3">
                {conversationalSearchEnabled
                  ? AI_SEARCH_OPTIONS.map((option) => (
                      <AISearchOption
                        key={option.id}
                        label={option.label}
                        iconName={option.iconName}
                        href={option.href}
                        onClick={
                          option.actionType === 'exit'
                            ? () => setConversationalSearchEnabled(false)
                            : undefined
                        }
                      />
                    ))
                  : prominentSearchOptions.map((link) => (
                      <div
                        role="button"
                        tabIndex={0}
                        key={link.id}
                        className={twMerge(
                          'text-base rounded-lg px-2 py-1 border-0',
                          contentType === link.id ? activeTab : inactiveTab
                        )}
                        style={
                          contentType !== link.id
                            ? { backgroundColor: 'rgba(0,0,0,0.15)' }
                            : undefined
                        }
                        data-value={link.id}
                        onClick={() => handleDropdownChange(link.id)}
                        onKeyDown={(event) =>
                          event.key === 'Enter' && handleDropdownChange(link.id)
                        }
                      >
                        <Text fontWeight="semibold" asChild>
                          <span>{link.label}</span>
                        </Text>
                      </div>
                    ))}
              </div>
            )}
            <form
              className={twMerge(
                'border-none rounded-full w-full lg:w-822px md:w-480px flex items-center bg-white',
                showAiCta ? 'pl-0 py-1' : 'pl-5'
              )}
              onSubmit={(event) => {
                event.preventDefault();
                setShowSuggestions(false);
                handleSubmit(contentType, searchTerm);
              }}
            >
              {showDropDown && (
                <>
                  <ProminentSearchDropdown
                    onChange={handleDropdownChange}
                    value={contentType}
                    useSnapshots={useSnapshots}
                    clearSuggestions={clearSuggestions}
                  />
                  <div>
                    <div className="w-px h-11 bg-gray-500" />
                  </div>
                </>
              )}
              {showAiCta && <SearchWithAIButton />}
              {isMediumScreen ? (
                <CrossAssetNavSearchBox
                  contentType={contentType}
                  setSearchTerm={setSearchTerm}
                  onSubmit={handleSubmit}
                  searchTerm={searchTerm}
                  suggestions={suggestions}
                  setSuggestions={setSuggestions}
                  clearSuggestions={clearSuggestions}
                  placeholder={getPlaceholder(
                    prominentSearchOptions.find(({ id }) => id === contentType)
                      ?.placeholderText
                  )}
                  isProminentSearch
                />
              ) : (
                <NavSearchBox
                  contentType={contentType}
                  onChange={setSearchTerm}
                  onSubmit={handleSubmit}
                  searchTerm={searchTerm}
                  placeholder={
                    prominentSearchOptions.find(({ id }) => id === contentType)
                      ?.placeholderText
                  }
                  userHasAnySubscription={userHasAnySubscription}
                  isProminentSearch
                />
              )}
              {showAiCta ? (
                <>
                  <button
                    type="button"
                    className={twMerge(
                      'flex flex-shrink-0 items-center gap-0.5 text-base text-gray-500 hover:text-gray-800 transition-colors duration-300 ease-in-out cursor-pointer bg-transparent border-0',
                      !conversationalSearchEnabled && 'hidden'
                    )}
                    onClick={() => {
                      // TODO (DSS-3768): open example picker
                    }}
                  >
                    <span className="whitespace-nowrap text-sm mr-1">
                      Try an example
                    </span>
                    <svg
                      width={13}
                      height={13}
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <RightArrowButton />
                </>
              ) : (
                <ProminentSearchButton />
              )}
            </form>
          </div>
        )}
      </NavSearchContext.Consumer>
    </NavSearchContextProvider>
  );
}
